import React from "react";
import { Box, Typography, Divider, Grid, Chip, Alert } from "@mui/material";
import AppModal from "../common/AppModal";
import DetailItem from "../common/DetailItem";
import StatusChip from "../common/StatusChip";
import AppCircularProgress from "../common/AppCircularProgress";
import { TimetableResponseDto } from "../../shared/dtos/timetable/TimetableResponseDto";

interface ViewTimetableModalProps {
  open: boolean;
  onClose: () => void;
  timetable: TimetableResponseDto | null;
  loading: boolean;
  error?: string | null;
}

const ViewTimetableModal: React.FC<ViewTimetableModalProps> = ({
  open,
  onClose,
  timetable,
  loading,
  error,
}) => {
  const getSlotsByDay = () => {
    if (!timetable?.slots) return {};

    const dayOrder: Record<string, number> = {
      MONDAY: 1,
      TUESDAY: 2,
      WEDNESDAY: 3,
      THURSDAY: 4,
      FRIDAY: 5,
      SATURDAY: 6,
      SUNDAY: 7,
    };

    const sorted = [...timetable.slots].sort((a, b) => {
      const aDay = a.dayOfWeek || "MONDAY";
      const bDay = b.dayOfWeek || "MONDAY";
      if (dayOrder[aDay] !== dayOrder[bDay]) {
        return dayOrder[aDay] - dayOrder[bDay];
      }
      return (a.startTime || "").localeCompare(b.startTime || "");
    });

    return sorted.reduce(
      (acc, slot) => {
        const day = slot.dayOfWeek || "Unknown";
        if (!acc[day]) acc[day] = [];
        acc[day].push(slot);
        return acc;
      },
      {} as Record<string, typeof timetable.slots>,
    );
  };

  const renderContent = (): React.ReactNode => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <AppCircularProgress />
        </Box>
      );
    }
    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }
    if (!timetable) {
      return (
        <Typography sx={{ textAlign: "center" }}>No data found.</Typography>
      );
    }

    const groupedSlots = getSlotsByDay();

    return (
      <Box>
        {/* Header Info */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <DetailItem
            label="Classroom"
            value={timetable.classroomName || "N/A"}
          />
          <DetailItem label="Term" value={timetable.termName || "N/A"} />
          <Grid size={{ xs: 2, sm: 6 }}>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 0.5 }}
            >
              Status
            </Typography>
            <StatusChip status={timetable.status} />
          </Grid>
          <DetailItem
            label="Generated On"
            value={new Date(timetable.createdAt).toLocaleString()}
          />
        </Grid>
        <Divider sx={{ mb: 2 }} />

        {/* Schedule Grid */}
        {Object.keys(groupedSlots).length === 0 ? (
          <Typography sx={{ color: "text.secondary", textAlign: "center" }}>
            No lessons scheduled yet.
          </Typography>
        ) : (
          Object.entries(groupedSlots).map(([day, slots]) => (
            <Box key={day} sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", mb: 1 }}
              >
                {day}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "repeat(3, 1fr)",
                  },
                  gap: 1.5,
                }}
              >
                {slots.map((slot) => (
                  <Box
                    key={slot.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                      boxShadow: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ display: "block", color: "text.secondary" }}
                    >
                      {slot.startTime?.substring(0, 5)} -{" "}
                      {slot.endTime?.substring(0, 5)}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                      {slot.subjectName}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: 12 }}>
                      {slot.teacherName}
                    </Typography>
                    {slot.location && (
                      <Chip
                        label={slot.location}
                        size="small"
                        variant="outlined"
                        sx={{ mt: 0.5, height: 20, fontSize: 10 }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          ))
        )}
      </Box>
    );
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={timetable?.name || "Timetable Details"}
      width="md"
    >
      {renderContent()}
    </AppModal>
  );
};

export default ViewTimetableModal;
